import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from "@backstage/core-components";
import { configApiRef, useApi } from "@backstage/core-plugin-api";
import { catalogLocationReadPermission } from "@backstage/plugin-catalog-common/alpha";
import { MyGroupsSidebarItem } from "@backstage/plugin-org";
import { usePermission } from "@backstage/plugin-permission-react";
import { taskCreatePermission } from "@backstage/plugin-scaffolder-common/alpha";
import { SidebarSearchModal } from "@backstage/plugin-search";
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from "@backstage/plugin-user-settings";
import { policyEntityCreatePermission } from "@backstage-community/plugin-rbac-common";
import { makeStyles } from "@material-ui/core";
import CreateComponentIcon from "@material-ui/icons/AddCircleOutline";
import CloudQueueIcon from "@material-ui/icons/CloudQueue";
import ExtensionIcon from "@material-ui/icons/Extension";
import HomeIcon from "@material-ui/icons/Home";
import MenuIcon from "@material-ui/icons/Menu";
import GroupIcon from "@material-ui/icons/People";
import SearchIcon from "@material-ui/icons/Search";
import SecurityIcon from "@material-ui/icons/Security";
import { cloudhealthExplorerReadPermission } from "@prisamedia/plugin-cloudhealth-explorer-common";
import type { PropsWithChildren } from "react";

import LogoFull from "./LogoFull";
import LogoIcon from "./LogoIcon";

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: "flex",
    flexFlow: "row nowrap",
    alignItems: "center",
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
  version: {
    color: "#7df3e1",
    fontSize: "0.65rem",
    opacity: 0.7,
    marginLeft: 24,
    marginTop: 2,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();
  const config = useApi(configApiRef);
  const version = config.getOptionalString("app.version") ?? "";

  return (
    <div className={classes.root}>
      <div>
        <Link
          to="/"
          underline="none"
          className={classes.link}
          aria-label="Home"
        >
          {isOpen ? <LogoFull /> : <LogoIcon />}
        </Link>
        {isOpen && <div className={classes.version}>v{version}</div>}
      </div>
    </div>
  );
};

const SidebarMenuItems = () => {
  const catalogAccess = usePermission({
    permission: catalogLocationReadPermission,
  });
  const scaffolderAccess = usePermission({
    permission: taskCreatePermission,
  });
  const rbacAccess = usePermission({
    permission: policyEntityCreatePermission,
  });
  const cloudExplorerAccess = usePermission({
    permission: cloudhealthExplorerReadPermission,
  });

  return (
    <>
      {(catalogAccess.loading || catalogAccess.allowed) && (
        <SidebarItem icon={HomeIcon} to="catalog" text="Home" />
      )}
      <MyGroupsSidebarItem
        singularTitle="My Group"
        pluralTitle="My Groups"
        icon={GroupIcon}
      />
      <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
      {(scaffolderAccess.loading || scaffolderAccess.allowed) && (
        <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." />
      )}
      {(rbacAccess.loading || rbacAccess.allowed) && (
        <SidebarItem icon={SecurityIcon} to="rbac" text="RBAC" />
      )}
      {(cloudExplorerAccess.loading || cloudExplorerAccess.allowed) && (
        <SidebarItem
          icon={CloudQueueIcon}
          to="cloud-explorer"
          text="Cloud Explorer"
        />
      )}
    </>
  );
};

export const Root = ({ children }: PropsWithChildren<object>) => (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Global nav, not org-specific */}
        <SidebarMenuItems />
        {/* End global nav */}
        <SidebarDivider />
        <SidebarScrollWrapper>
          {/* Items in this group will be scrollable if they run out of space */}
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <SidebarDivider />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarSettings />
      </SidebarGroup>
    </Sidebar>
    {children}
  </SidebarPage>
);
